import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Avatar, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const ProfilePage = () => {
  const { currentUser, logout } = useAuth();
  
  // Mock user data - in a real app, this would come from the user context
  const userData = {
    name: currentUser?.name || 'John Doe',
    email: currentUser?.email || 'john.doe@example.com',
    phone: currentUser?.phone || '+1 (555) 123-4567',
    address: currentUser?.address || '123 Main St, New York, NY 10001',
    joinDate: currentUser?.createdAt || '2023-01-15',
    assetsCount: 3,
    activeAlertsCount: 1
  };

  return (
    <Box sx={{ pb: 7 }}>
      {/* Profile Header */}
      <Paper sx={{ mb: 3, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.main',
              fontSize: '2rem',
              mr: 2
            }}
          >
            {userData.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {userData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Client Account
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
              <CardContent>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                  {userData.assetsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tracked Assets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ bgcolor: 'error.50', height: '100%' }}>
              <CardContent>
                <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                  {userData.activeAlertsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Alerts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Contact Information */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Contact Information
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem>
            <ListItemIcon>
              <EmailIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Email" 
              secondary={userData.email}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              secondaryTypographyProps={{ variant: 'body1' }}
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemIcon>
              <PhoneIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Phone" 
              secondary={userData.phone}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              secondaryTypographyProps={{ variant: 'body1' }}
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemIcon>
              <LocationIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Address" 
              secondary={userData.address}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              secondaryTypographyProps={{ variant: 'body1' }}
            />
          </ListItem>
        </List>
      </Paper>
      
      {/* Account Settings */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Account Settings
          </Typography>
        </Box>
        <List disablePadding>
          <ListItem button>
            <ListItemIcon>
              <PersonIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Edit Profile" />
          </ListItem>
          <Divider component="li" />
          <ListItem button>
            <ListItemIcon>
              <SecurityIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Security Settings" />
          </ListItem>
          <Divider component="li" />
          <ListItem button>
            <ListItemIcon>
              <SettingsIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Preferences" />
          </ListItem>
        </List>
      </Paper>
      
      {/* Logout Button */}
      <Button 
        variant="outlined" 
        color="error" 
        fullWidth 
        startIcon={<LogoutIcon />}
        onClick={logout}
        sx={{ mb: 2 }}
      >
        Logout
      </Button>
    </Box>
  );
};

export default ProfilePage;
